import { useState } from 'react';
import { Link } from 'react-router-dom';
import { MapPin, Phone, Mail, Clock, Facebook, Instagram, MessageCircle } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { toast } from 'sonner';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

export default function ContactPage() {
  const [form, setForm] = useState({ name: '', email: '', phone: '', message: '', privacy: false });
  const [submitting, setSubmitting] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.privacy) {
      toast.error('Devi accettare la Privacy Policy per continuare');
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(`${API}/contact`, {
        name: form.name,
        email: form.email,
        phone: form.phone,
        message: form.message,
      });
      toast.success('Messaggio inviato con successo!');
      setForm({ name: '', email: '', phone: '', message: '', privacy: false });
    } catch (error) {
      toast.error('Errore nell\'invio del messaggio');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div data-testid="contact-page">
      {/* Hero */}
      <section className="relative pt-32 pb-24 bg-primary">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl animate-fadeIn">
            <p className="text-xs font-sans uppercase tracking-[0.2em] font-medium text-accent mb-4">
              Contatti
            </p>
            <h1 className="font-serif text-5xl md:text-6xl text-white tracking-tight leading-[1.1] mb-6">
              Parliamo del tuo progetto
            </h1>
            <p className="font-sans text-lg text-white/80 leading-relaxed">
              Siamo qui per aiutarti. Contattaci per una consulenza gratuita o vieni a trovarci in sede.
            </p>
          </div>
        </div>
      </section>

      {/* Content */}
      <section className="py-24 bg-background">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-16">
            {/* Contact Info */}
            <div>
              <h2 className="font-serif text-3xl md:text-4xl text-text-main mb-8">
                Informazioni di contatto
              </h2>
              
              <div className="space-y-6 mb-12">
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 bg-primary/10 rounded-xl flex items-center justify-center flex-shrink-0">
                    <MapPin size={24} className="text-primary" />
                  </div>
                  <div>
                    <h3 className="font-sans font-medium text-text-main mb-1">Indirizzo</h3>
                    <p className="font-sans text-text-muted">
                      Via Caduti sul Lavoro n.49<br />
                      81100 Caserta (CE), Italia
                    </p>
                  </div>
                </div>

                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 bg-primary/10 rounded-xl flex items-center justify-center flex-shrink-0">
                    <Phone size={24} className="text-primary" />
                  </div>
                  <div>
                    <h3 className="font-sans font-medium text-text-main mb-1">Telefono</h3>
                    <a href="tel:+3908231848006" className="font-sans text-text-muted hover:text-primary transition-colors">
                      0823 1848006
                    </a>
                  </div>
                </div>

                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 bg-primary/10 rounded-xl flex items-center justify-center flex-shrink-0">
                    <Phone size={24} className="text-primary" />
                  </div>
                  <div>
                    <h3 className="font-sans font-medium text-text-main mb-1">Cellulare</h3>
                    <a href="tel:+393292435449" className="font-sans text-text-muted hover:text-primary transition-colors">
                      +39 329 2435449
                    </a>
                  </div>
                </div>

                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 bg-primary/10 rounded-xl flex items-center justify-center flex-shrink-0">
                    <MessageCircle size={24} className="text-primary" />
                  </div>
                  <div>
                    <h3 className="font-sans font-medium text-text-main mb-1">WhatsApp</h3>
                    <a href="https://wa.me/393755148808" target="_blank" rel="noopener noreferrer" className="font-sans text-text-muted hover:text-primary transition-colors">
                      +39 375 5148808
                    </a>
                  </div>
                </div>

                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 bg-primary/10 rounded-xl flex items-center justify-center flex-shrink-0">
                    <Mail size={24} className="text-primary" />
                  </div>
                  <div>
                    <h3 className="font-sans font-medium text-text-main mb-1">Email</h3>
                    <a href="mailto:info@greenhousecaserta.it" className="font-sans text-text-muted hover:text-primary transition-colors">
                      info@greenhousecaserta.it
                    </a>
                  </div>
                </div>

                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 bg-primary/10 rounded-xl flex items-center justify-center flex-shrink-0">
                    <Clock size={24} className="text-primary" />
                  </div>
                  <div>
                    <h3 className="font-sans font-medium text-text-main mb-1">Orari di apertura</h3>
                    <p className="font-sans text-text-muted">
                      Lunedì - Venerdì: 09:00 - 13:00 / 15:30 - 20:00<br />
                      Sabato: 09:00 - 13:00<br />
                      Domenica: Chiuso
                    </p>
                  </div>
                </div>
              </div>

              {/* Social Links */}
              <div>
                <h3 className="font-sans font-medium text-text-main mb-4">Seguici sui social</h3>
                <div className="flex gap-4">
                  <a
                    href="https://www.facebook.com/greenhousesrl"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="w-12 h-12 bg-primary/10 rounded-xl flex items-center justify-center hover:bg-primary hover:text-white transition-all"
                    data-testid="contact-facebook"
                  >
                    <Facebook size={24} />
                  </a>
                  <a
                    href="https://www.instagram.com/greenhouse_caserta"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="w-12 h-12 bg-primary/10 rounded-xl flex items-center justify-center hover:bg-primary hover:text-white transition-all"
                    data-testid="contact-instagram"
                  >
                    <Instagram size={24} />
                  </a>
                </div>
              </div>
            </div>

            {/* Contact Form */}
            <div className="bg-white p-8 rounded-xl shadow-lg" data-testid="contact-form">
              <h3 className="font-serif text-2xl text-text-main mb-6">Inviaci un messaggio</h3>
              <form onSubmit={handleSubmit} className="space-y-4">
                <Input
                  placeholder="Nome e Cognome *"
                  value={form.name}
                  onChange={(e) => setForm({ ...form, name: e.target.value })}
                  required
                  data-testid="contact-form-name"
                />
                <Input
                  type="email"
                  placeholder="Email *"
                  value={form.email}
                  onChange={(e) => setForm({ ...form, email: e.target.value })}
                  required
                  data-testid="contact-form-email"
                />
                <Input
                  type="tel"
                  placeholder="Telefono *"
                  value={form.phone}
                  onChange={(e) => setForm({ ...form, phone: e.target.value })}
                  required
                  data-testid="contact-form-phone"
                />
                <Textarea
                  placeholder="Il tuo messaggio *"
                  value={form.message}
                  onChange={(e) => setForm({ ...form, message: e.target.value })}
                  rows={5}
                  required
                  data-testid="contact-form-message"
                />
                <label className="flex items-start gap-2 text-sm font-sans text-text-muted cursor-pointer">
                  <input
                    type="checkbox"
                    checked={form.privacy}
                    onChange={(e) => setForm({ ...form, privacy: e.target.checked })}
                    required
                    className="mt-0.5 w-4 h-4 accent-primary flex-shrink-0"
                    data-testid="contact-form-privacy"
                  />
                  <span>
                    Dichiaro di aver letto e accetto la{' '}
                    <a href="https://www.iubenda.com/privacy-policy/84083459" className="text-primary underline hover:text-primary-hover" target="_blank" rel="noopener noreferrer">
                      Privacy Policy
                    </a>
                    {' '}(GDPR) *
                  </span>
                </label>
                <Button
                  type="submit"
                  className="w-full bg-primary hover:bg-primary-hover"
                  disabled={submitting}
                  data-testid="contact-form-submit"
                >
                  {submitting ? 'Invio in corso...' : 'Invia Messaggio'}
                </Button>
                <p className="font-sans text-xs text-text-muted text-center">
                  * Campi obbligatori. I tuoi dati saranno trattati nel rispetto della privacy.
                </p>
              </form>
            </div>
          </div>
        </div>
      </section>

      {/* Map */}
      <section className="h-[400px] bg-gray-100" data-testid="contact-map">
        <iframe
          src="https://www.openstreetmap.org/export/embed.html?bbox=14.32%2C41.07%2C14.35%2C41.09&layer=mapnik&marker=41.08%2C14.335"
          width="100%"
          height="100%"
          style={{ border: 0 }}
          allowFullScreen=""
          loading="lazy"
          title="Posizione Green House"
        />
      </section>
    </div>
  );
}
